import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import {
    Search,
    CheckCircle,
    Clock,
    XCircle,
    RefreshCw,
    TrendingUp,
    TrendingDown,
    BarChart3
} from 'lucide-react';

const AdminSpotTradesPage = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sideFilter, setSideFilter] = useState('');
    const [pagination, setPagination] = useState({ total: 0, limit: 50, offset: 0 });

    useEffect(() => {
        fetchOrders();
    }, [statusFilter, sideFilter]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (statusFilter) params.append('status', statusFilter);
            if (sideFilter) params.append('side', sideFilter);
            params.append('limit', pagination.limit);
            params.append('offset', pagination.offset);

            const res = await api.get(`/admin/orders?${params.toString()}`);
            setOrders(res.data.orders || []);
            setPagination(res.data.pagination || pagination);
        } catch (error) {
            console.error('Fetch orders error:', error);
            toast.error('Failed to load orders');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            filled: 'bg-green-500/20 text-green-400 border-green-500/30',
            open: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            partially_filled: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
            cancelled: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
            rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
            expired: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
        };
        const icons = {
            filled: <CheckCircle size={14} />,
            open: <Clock size={14} />,
            pending: <Clock size={14} />,
            partially_filled: <BarChart3 size={14} />,
            cancelled: <XCircle size={14} />,
            rejected: <XCircle size={14} />,
            expired: <XCircle size={14} />
        };
        const labels = {
            filled: 'Filled',
            open: 'Open',
            pending: 'Pending',
            partially_filled: 'Partial',
            cancelled: 'Cancelled',
            rejected: 'Rejected',
            expired: 'Expired'
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
                {icons[status] || icons.pending}
                {labels[status] || status}
            </span>
        );
    };

    const filteredOrders = orders.filter(o => {
        if (!searchTerm) return true;
        const search = searchTerm.toLowerCase();
        return (
            o.user?.email?.toLowerCase().includes(search) ||
            o.tradingPair?.symbol?.toLowerCase().includes(search)
        );
    });

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />

            {/* Header */}
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                            <BarChart3 className="text-blue-400" /> Spot Trades
                        </h1>
                        <p className="text-gray-400 text-sm">View all user trading orders</p>
                    </div>
                    <button
                        onClick={fetchOrders}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
                    >
                        <RefreshCw size={18} /> Refresh
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 bg-slate-800/50 border-b border-slate-700">
                <div className="flex flex-wrap gap-4">
                    <div className="relative flex-1 max-w-md">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search by email or trading pair..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-blue-500"
                        />
                    </div>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Status</option>
                        <option value="open">Open</option>
                        <option value="pending">Pending</option>
                        <option value="filled">Filled</option>
                        <option value="partially_filled">Partially Filled</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="rejected">Rejected</option>
                        <option value="expired">Expired</option>
                    </select>
                    <select
                        value={sideFilter}
                        onChange={(e) => setSideFilter(e.target.value)}
                        className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                    >
                        <option value="">All Sides</option>
                        <option value="buy">Buy</option>
                        <option value="sell">Sell</option>
                    </select>
                </div>
            </div>

            {/* Table */}
            <div className="p-6">
                <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-400">Loading orders...</div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="text-center py-12 text-gray-400">No orders found</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3 font-medium">User</th>
                                        <th className="px-4 py-3 font-medium">Pair</th>
                                        <th className="px-4 py-3 font-medium">Side</th>
                                        <th className="px-4 py-3 font-medium">Type</th>
                                        <th className="px-4 py-3 font-medium">Price</th>
                                        <th className="px-4 py-3 font-medium">Quantity</th>
                                        <th className="px-4 py-3 font-medium">Filled</th>
                                        <th className="px-4 py-3 font-medium">Status</th>
                                        <th className="px-4 py-3 font-medium">Date</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-slate-700/30 transition">
                                            <td className="px-4 py-3">
                                                <div>
                                                    <div className="text-white font-medium text-sm">{order.user?.email || 'Unknown'}</div>
                                                    <div className="text-xs text-gray-400">
                                                        {order.user?.firstName} {order.user?.lastName}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="text-white font-medium">
                                                    {order.tradingPair?.symbol || 'Unknown'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${order.side === 'buy'
                                                    ? 'bg-green-500/20 text-green-400'
                                                    : 'bg-red-500/20 text-red-400'
                                                    }`}>
                                                    {order.side === 'buy' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                                                    {order.side?.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-gray-300 text-sm capitalize">
                                                {order.type?.replace('_', ' ')}
                                            </td>
                                            <td className="px-4 py-3 text-white font-mono text-sm">
                                                {order.price ? parseFloat(order.price).toFixed(6) : 'Market'}
                                            </td>
                                            <td className="px-4 py-3 text-white font-mono text-sm">
                                                {parseFloat(order.quantity).toFixed(6)}
                                            </td>
                                            <td className="px-4 py-3 text-gray-300 font-mono text-sm">
                                                {parseFloat(order.filledQuantity || 0).toFixed(6)}
                                            </td>
                                            <td className="px-4 py-3">
                                                {getStatusBadge(order.status)}
                                            </td>
                                            <td className="px-4 py-3 text-gray-400 text-sm">
                                                {new Date(order.createdAt).toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination Info */}
                <div className="mt-4 text-center text-gray-400 text-sm">
                    Showing {filteredOrders.length} of {pagination.total} orders
                </div>
            </div>
        </div>
    );
};

export default AdminSpotTradesPage;
